'use client'

import { tw } from 'utils/classnames'
import { Ribbon } from '@repo/web-ui/components/ribbon'
import { useTranslations } from 'next-intl'
import { useUserQuery } from 'auth/hooks'
import { isLoggedInQuerySelector } from 'auth/selectors'
import { Link } from '@repo/web-ui/components/link'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { usePaymentsDialog } from 'payments/components/payments-dialog-wrapper-context'
import { sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'
import { LocalizedPrice } from '@repo/web-ui/components/localized-price/localized-price'
import { usePricesForUser } from '@repo/cf-user/hooks/use-prices'
import {
  PAYMENT_ENTRY_POINTS,
  savePaymentModalEntryPoint,
} from 'payments/payments-dialog/analytics'
import { StartFreeTrialButton } from 'payments/start-free-trial-button'

import { FreeIcon, AllAccessIcon, AllAccessYearlyIcon } from './icons'

type WidgetType = 'free_trial' | 'all_access_monthly' | 'all_access_yearly'

interface WidgetProps {
  type: WidgetType
  variant?: 'small' | 'normal'
}

const getIcon = (type: WidgetType) => {
  if (type === 'free_trial') {
    return <FreeIcon className="size-9 flex-shrink-0" />
  }

  if (type === 'all_access_monthly') {
    return <AllAccessIcon className="size-9 flex-shrink-0" />
  }

  return <AllAccessYearlyIcon className="h-7 w-[auto] flex-shrink-0" />
}

const getSubprice = (type: WidgetType) => {
  if (type !== 'all_access_monthly') {
    return `${type}.sub_price`
  }

  return null
}

const getWidgetLink = (type: WidgetType) => {
  if (type === 'free_trial') {
    return AppRoutes.B_FREE_TRIAL
  }

  if (type === 'all_access_monthly') {
    return AppRoutes.B_MONTHLY
  }

  return AppRoutes.B_YEARLY_PROMO
}

const SUBSCRIPTION_PAGE_PLAN_CLICKED = 'Subscription Page Plan Clicked'
const anchorPrice = 35.09

// oxlint-disable-next-line complexity
export function Widget({ variant = 'normal', type }: WidgetProps) {
  const t = useTranslations('pages.subscription.subscriptions')
  const { data: userData } = useUserQuery()
  const isUserLoggedIn = isLoggedInQuerySelector(userData)
  const { prices, currency } = usePricesForUser()

  const isEligibleForAllAccessFreeTrial =
    userData?.me?.user?.subscriptionAccess.allAccess.isEligibleForFreeTrial

  const { open } = usePaymentsDialog()

  //TODO: Replace it with useProductCountsQuery data once we move to server side rendering
  //Represents the sum of embroidery, fonts, crafts, and graphics
  const productCount = 11_739_162

  const price =
    // oxlint-disable-next-line no-nested-ternary
    type === 'free_trial'
      ? 0
      : type === 'all_access_monthly'
        ? prices.monthly
        : prices.yearlyPromoMonthlySplit

  const subPrice = getSubprice(type)

  const isFree = price === 0
  const isMostPopular = type === 'all_access_monthly'

  const Icon = getIcon(type)

  const handleButtonClick = () => {
    sendAmplitudeEvent(SUBSCRIPTION_PAGE_PLAN_CLICKED, {
      type: type,
    })
  }

  const handleYearlyButtonClick = () => {
    savePaymentModalEntryPoint(PAYMENT_ENTRY_POINTS.SUBSCRIPTION_PAGE)
    handleButtonClick()

    if (isUserLoggedIn) {
      open(type === 'all_access_yearly' ? 'yearly' : 'freeTrial')
    }
  }

  if (isUserLoggedIn && !isEligibleForAllAccessFreeTrial && type === 'free_trial') {
    return null
  }

  return (
    <div
      className={tw(
        isMostPopular
          ? 'border-2 border-[#493675] bg-[#f6f7fb] px-[9px]'
          : 'border border-[#DFDDE5] px-[10px]',
        'relative flex w-full flex-col rounded pt-5 md:px-[22px] md:pt-[42px]',
      )}
    >
      <div>
        <div className="flex items-center justify-between">
          <div className={tw('flex min-h-[60px] flex-col items-center gap-[10px] md:flex-row')}>
            {Icon}
            <p className={tw(variant === 'normal' ? 'text-20' : 'md:text-20 text-14', 'font-bold')}>
              {t(`${type}.name`)}
            </p>
          </div>
          {isMostPopular && variant === 'normal' ? (
            <div className="border-secondary-500 text-secondary-500 hover:bg-secondary-500 hover:text-cf-white cursor-default rounded border px-2 py-1">
              {t(`${type}.tag`)}
            </div>
          ) : null}
        </div>

        {variant === 'normal' ? (
          <div className="text-16 mt-5 min-h-[120px]">
            {t.rich(`${type}.copy`, {
              br: () => <br />,
              number: () => productCount.toLocaleString(),
            })}
          </div>
        ) : null}
      </div>

      <div className="mt-3 flex h-full flex-col md:mt-5">
        <div className="flex h-full flex-col justify-end">
          {isFree ? (
            <p
              className={tw(
                variant === 'normal' ? 'text-[32px]' : 'md:text-16 text-14 block md:inline',
                'font-bold',
              )}
            >
              {t('free_trial.price')} {`(${currency.symbol}0.00)`}
            </p>
          ) : (
            <span
              className={tw(
                variant === 'normal' ? 'text-12' : 'md:text-14 text-[8px]',
                'font-bold',
              )}
            >
              {t.rich('month_anchor', {
                span: str => <span className="uppercase">{str}</span>,
                price: () => (
                  <LocalizedPrice
                    className={tw(
                      variant === 'normal' ? 'text-[32px]' : 'md:text-16 text-14 inline',
                    )}
                    value={price}
                  />
                ),
                anchorPrice,
                strike: str => (
                  <div className="text-14 inline line-through">
                    {currency.symbol}
                    {str}
                  </div>
                ),
              })}
            </span>
          )}
          {subPrice ? (
            <p
              suppressHydrationWarning
              className={tw(
                !subPrice && 'opacity-0',
                variant === 'normal' ? 'text-14' : 'md:text-12 text-[10px]',
                'font-bold',
              )}
            >
              {t(subPrice, {
                currency: currency.symbol,
                price: prices.yearlyPromo,
              }) || '&nbsp'}
            </p>
          ) : null}
        </div>
        <div className="mt-5 flex w-full flex-col items-center">
          {type === 'free_trial' ? (
            <StartFreeTrialButton
              onClick={handleButtonClick}
              entryPoint={PAYMENT_ENTRY_POINTS.SUBSCRIPTION_PAGE}
              label={t('free_trial.cta_try_for_free')}
              className={tw(
                isMostPopular
                  ? 'bg-primary-500 hover:bg-primary-700 text-cf-white'
                  : 'bg-primary-100 hover:bg-aubergine-300',
                variant === 'normal' ? 'text-14 px-6 py-2' : 'text-10 lg:text-14 px-4 py-[10px]',
                'flex h-[34px] w-full items-center justify-center rounded text-center font-bold uppercase transition-all',
              )}
            />
          ) : isUserLoggedIn ? (
            <button
              className={tw(
                isMostPopular
                  ? 'bg-primary-500 hover:bg-primary-700 text-cf-white'
                  : 'bg-primary-100 hover:bg-aubergine-300',
                variant === 'normal' ? 'text-14 px-6 py-2' : 'text-10 lg:text-14 px-4 py-[10px]',
                'flex h-[34px] w-full items-center justify-center rounded text-center font-bold uppercase transition-all',
              )}
              onClick={handleYearlyButtonClick}
            >
              {t(`${type}.cta`)}
            </button>
          ) : (
            <Link
              className={tw(
                isMostPopular
                  ? 'bg-primary-500 hover:bg-primary-700 text-cf-white'
                  : 'bg-primary-100 hover:bg-aubergine-300',
                variant === 'normal' ? 'text-14 px-6 py-2' : 'text-10 lg:text-14 px-4 py-[10px]',
                'flex h-[34px] w-full items-center justify-center rounded text-center font-bold uppercase transition-all',
              )}
              onClick={handleYearlyButtonClick}
              href={getWidgetLink(type)}
            >
              {t(`${type}.cta`)}
            </Link>
          )}

          <p
            className={tw(
              !isFree || 'opacity-100',
              'md:text-12 mb-5 mt-[5px] text-center text-[8px] font-normal text-gray-500',
            )}
          >
            {t('footnote_2')}
          </p>
        </div>
      </div>
      {type === 'all_access_yearly' ? (
        <Ribbon
          text={t(`${type}.tag`)}
          color="#c7254e"
          className="text-cf-white md:text-12 text-10 !-top-[15px] flex items-center justify-center pl-5 pr-4"
        />
      ) : null}
    </div>
  )
}
